import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { TextLink } from '@components/common/Editorial';
import { ProductMedia } from './ProductMedia';
import { ProductPurchase } from './ProductPurchase';
export const ProductCard = ({ product }) => {
    const { t } = useTranslation();
    const software = product.category === 'software';
    return (_jsxs(Offer, { "$software": software || !!product.variants, "aria-labelledby": `catalog-${product.id}`, children: [_jsx(Media, { product: product, caption: software }), _jsxs(OfferTitle, { children: [_jsxs(Category, { children: [_jsx("span", { children: t(`productsPage.categories.${product.category}`) }), _jsx("span", { children: t(product.availability === 'coming_soon'
                                    ? 'availability.comingSoon'
                                    : product.id === 'personal-care-360'
                                        ? 'personalCare360.premium'
                                        : product.variants
                                            ? 'planComparison.eyebrow'
                                            : product.commercial
                                                ? 'productsPage.commercialOffer'
                                                : 'home.software.demo') })] }), _jsx("h3", { id: `catalog-${product.id}`, children: t(product.nameKey) })] }), _jsxs(OfferDescription, { children: [_jsx("p", { children: t(product.descriptionKey) }), _jsxs(TextLink, { to: `/products/${product.slug}`, children: [t('actions.viewDetails'), _jsx(ArrowUpRight, { size: 18, "aria-hidden": "true" })] })] }), _jsx(Purchase, { product: product })] }));
};
const Media = styled(ProductMedia) `
  grid-area: image;
`;
const OfferTitle = styled.div `
  grid-area: title;
  min-width: 0;
  h3 {
    margin: 0;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: clamp(1.4rem, 2vw, 1.75rem);
    font-weight: 400;
    line-height: 1.2;
    letter-spacing: -0.035em;
    overflow-wrap: break-word;
  }
`;
const Category = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-bottom: 0.75rem;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.blue600};
  letter-spacing: 0.07em;
  text-transform: uppercase;
  span:last-child {
    color: ${({ theme }) => theme.colors.muted};
    text-transform: none;
    letter-spacing: 0;
  }
`;
const OfferDescription = styled.div `
  grid-area: description;
  min-width: 0;
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1rem;
    line-height: 1.75;
  }
  ${TextLink} {
    margin-top: 0.75rem;
  }
`;
const Purchase = styled(ProductPurchase) `
  grid-area: purchase;
  align-self: start;
  border-left: 1px solid ${({ theme }) => theme.colors.line};
  padding-left: 1.5rem;
`;
const Offer = styled.article `
  display: grid;
  grid-template-columns: 210px minmax(0, 1fr) 238px;
  grid-template-areas: 'image title purchase' 'image description purchase';
  grid-template-rows: min-content 1fr;
  gap: 1rem 2rem;
  padding-block: 2.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  @media (min-width: 768px) and (max-width: 1099px) {
    grid-template-columns: 170px minmax(0, 1fr);
    grid-template-areas: 'image title' 'image description' 'image purchase';
    ${Purchase} {
      border: 0;
      padding: 0;
    }
  }
  @media (max-width: 767px) {
    grid-template-columns: 104px minmax(0, 1fr);
    grid-template-areas: 'image title' 'description description' 'purchase purchase';
    gap: 1.25rem 1rem;
    ${OfferTitle} h3 {
      font-size: 1.25rem;
    }
    ${Purchase} {
      border: 0;
      padding: 0;
    }
  }
  ${({ $software }) => $software &&
    css `
      grid-template-columns: minmax(0, 1fr) minmax(0, 0.85fr);
      grid-template-areas: 'title image' 'description image' 'purchase image';
      grid-template-rows: auto auto 1fr;
      gap: 1.5rem 4rem;
      padding-block: 2.5rem;
      ${OfferTitle} h3 {
        font-size: clamp(1.75rem, 3vw, 2.75rem);
      }
      ${Media} {
        justify-self: end;
        align-self: center;
      }
      ${Purchase} {
        border: 0;
        padding: 1.5rem 0 0;
        border-top: 1px solid ${({ theme }) => theme.colors.line};
      }
      @media (min-width: 768px) and (max-width: 1099px) {
        grid-template-columns: 1fr 1fr;
        grid-template-areas: 'title image' 'description image' 'purchase purchase';
        gap: 1.5rem 2rem;
      }
      @media (max-width: 767px) {
        grid-template-columns: minmax(0, 1fr);
        grid-template-areas: 'title' 'image' 'description' 'purchase';
        ${OfferTitle} h3 {
          font-size: 2rem;
        }
      }
    `}
`;
